/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * RAG view insight panels — the dependency graph, the query ledger and the
 * observability of one RAG, as self-contained cards matching the Prompt view's
 * look (ui-panel surfaces, inline trace viewer, deep-link to AI Observability).
 *
 *   - RagDependenciesPanel   — what references this RAG (agents, conversations,
 *     eval suites), from /rags/{id}/dependents.
 *   - RagQueryHistoryPanel   — the RAG's production queries (cost, latency, the
 *     version that answered), from /rags/{id}/queries.
 *   - RagObservabilityPanel  — a health summary over the RAG's traces, each
 *     openable in full inline, with a deep-link to AI Observability.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { formatDate } from 'sharedUi/dateUtils';

import { ragsService, type RagDependentGroup } from '../../services/rags.service';
import { aiObservabilityService } from '../../services/ai-observability.service';
import { fmtCost, fmtMs, type ObsTraceRow } from '../../types/ai-observability.types';
import ObsTracingDetail from '../../pages/ai-observability/obs-tracing-detail';

/** Base64 URL-safe id encoding — matches useSecureNavigation so a built link
 *  lands on the same encoded view route the app navigates to internally. */
const encodeId = (id: string): string => {
  try {
    return btoa(id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch {
    return id;
  }
};

/** Dependent types whose row id maps cleanly to a view route. Conversations have
 *  no stable view, so those are shown as plain chips rather than broken links. */
const LINK_BASE: Record<string, string> = { agent: '/agents', eval_suite: '/evals' };

/** Every master/collection that can reference a RAG — always listed (0 when
 *  none), so a viewer or a demo sees all the places a RAG can be used. */
const DEP_CATEGORIES: { type: RagDependentGroup['type']; label: string }[] = [
  { type: 'agent', label: 'Agents' },
  { type: 'conversation', label: 'Conversations' },
  { type: 'eval_suite', label: 'Eval suites' },
];

/** A chip that becomes a link to the record's view when a base + id are known. */
/** A subtle, truncating reference pill — a link when base + id are known. */
function RefChip({ base, id, label }: Readonly<{ base?: string; id?: string | null; label: string }>) {
  const cls = 'inline-flex max-w-[260px] items-center truncate rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-ink transition-colors dark:border-gray-700 dark:bg-gray-800/60';
  if (base && id) {
    return <Link to={`${base}/${encodeId(id)}`} title={label}
      className={`${cls} hover:border-blue-400 hover:text-blue-600`}>{label}</Link>;
  }
  return <span title={label} className={cls}>{label}</span>;
}

export function RagDependenciesPanel({ ragId }: Readonly<{ ragId: string }>) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['rag-dependents', ragId],
    queryFn: () => ragsService.getDependents(ragId),
    enabled: !!ragId,
    staleTime: 30 * 1000,
  });

  const byType = new Map((data?.groups ?? []).map((g) => [g.type, g]));

  return (
    <div className="ui-panel p-5" data-testid="RagView.Dependencies">
      <h2 className="text-sm font-semibold text-ink mb-0.5">Used by</h2>
      <p className="text-xs text-ink-muted mb-3">
        Everything across the platform that references this RAG. Click any name to open its record.
      </p>
      <span className="text-xs text-ink-muted block mb-1">
        {data ? `${data.total} reference${data.total === 1 ? '' : 's'} in total` : 'References'}
      </span>
      {isLoading && <p className="text-sm text-ink-muted">Loading dependents…</p>}
      {isError && <p className="text-sm text-ink-muted">Could not load dependents.</p>}
      {!isLoading && !isError && (
        <div className="flex flex-col gap-2">
          {DEP_CATEGORIES.map((cat) => {
            const g = byType.get(cat.type);
            const count = g?.count ?? 0;
            return (
              <div key={cat.type}>
                <div className="text-xs font-medium text-ink mb-1">{cat.label} · {count}</div>
                {count > 0 && g ? (
                  <div className="flex flex-wrap gap-1.5">
                    {g.items.map((it, i) => (
                      <RefChip key={it.id ?? i} base={LINK_BASE[g.type]} id={it.id} label={it.name} />
                    ))}
                    {g.count > g.items.length && (
                      <span className="text-xs text-ink-muted self-center">+{g.count - g.items.length} more</span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-ink-muted">None</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const vStatusSeverity = (s?: string | null): 'success' | 'info' | 'warning' | 'danger' => {
  const v = (s || '').toUpperCase();
  if (v === 'ACTIVE') return 'success';
  if (v === 'CANDIDATE') return 'info';
  if (v === 'EVAL_FAILED') return 'danger';
  return 'warning';
};

export function RagQueryHistoryPanel({ ragId }: Readonly<{ ragId: string }>) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['rag-query-history', ragId],
    queryFn: () => ragsService.listQueries(ragId, { page: 1, page_size: 10 }),
    enabled: !!ragId,
    staleTime: 30 * 1000,
  });

  const rows = data?.items ?? [];

  return (
    <div className="ui-panel p-5" data-testid="RagView.QueryHistory">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink">Query history</h2>
          <p className="text-xs text-ink-muted">
            Production queries this RAG answered — the version, cost, latency and outcome of each.
          </p>
        </div>
        {data && data.total > 0 && (
          <span className="text-xs text-ink-muted whitespace-nowrap">{data.total} total</span>
        )}
      </div>

      {isLoading && <p className="text-sm text-ink-muted">Loading queries…</p>}
      {isError && <p className="text-sm text-ink-muted">Could not load query history.</p>}
      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-sm text-ink-muted">
          No queries yet. Use the <span className="font-medium">Playground</span> to ask this RAG a question.
        </p>
      )}
      {!isLoading && rows.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm text-ink" title={r.query ?? undefined}>
                  {r.query || '(no question text)'}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-ink-muted">
                  <span className="whitespace-nowrap">{formatDate(r.createdAt)}</span>
                  {r.model && <><span>·</span><span className="truncate">{r.model}</span></>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3 text-xs text-ink-muted">
                <span className="whitespace-nowrap">{(r.chunkCount ?? 0)} chunks</span>
                <span className="whitespace-nowrap">{fmtCost(r.cost, r.currency)}</span>
                <span className="whitespace-nowrap">{fmtMs(r.totalMs)}</span>
                {r.version != null && (
                  <Tag value={`v${r.version}`} severity={vStatusSeverity(r.versionStatus)}
                    className="text-xs px-1.5 py-0.5" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const OBS_RANGES = [
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
];

/** p-th percentile of a list, or null when empty. */
const percentile = (xs: number[], p: number): number | null => {
  if (!xs.length) return null;
  const sorted = [...xs].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
};

export function RagObservabilityPanel({ ragCode }: Readonly<{ ragCode: string }>) {
  const [range, setRange] = useState('7d');
  const [openTrace, setOpenTrace] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['rag-observability', ragCode, range],
    queryFn: () => aiObservabilityService.getTracing({ ragCode, timeRange: range, limit: 50 }),
    enabled: !!ragCode,
    staleTime: 30 * 1000,
  });

  const rows: ObsTraceRow[] = data?.traces ?? [];
  const summary = data?.summary;

  const kpis = useMemo(() => {
    const calls = summary?.calls ?? 0;
    const failed = summary?.failed ?? 0;
    const latencies = rows.map((r) => r.latencyMs).filter((v): v is number => v != null);
    const costs = rows.map((r) => r.cost).filter((v): v is number => v != null);
    const currencies = new Set(rows.map((r) => r.currency).filter(Boolean));
    const totalCost = costs.length ? costs.reduce((a, b) => a + b, 0) : null;
    const avg = latencies.length ? latencies.reduce((a, b) => a + b, 0) / latencies.length : null;
    return {
      calls,
      successRate: calls > 0 ? `${Math.round(((calls - failed) / calls) * 100)}%` : '—',
      avgLatency: fmtMs(avg),
      p95Latency: fmtMs(percentile(latencies, 95)),
      totalCost: fmtCost(totalCost, currencies.size === 1 ? [...currencies][0] : null),
    };
  }, [summary, rows]);

  const deepLink = `/ai-observability/tracing?ragCode=${encodeURIComponent(ragCode)}`;

  return (
    <div className="ui-panel p-5" data-testid="RagView.Observability">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink">Observability</h2>
          <p className="text-xs text-ink-muted">
            Health of this RAG's traced queries. Open any trace for its full step-by-step waterfall.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            className="text-xs border border-gray-200 dark:border-gray-700 rounded px-1.5 py-1 bg-transparent text-ink"
            value={range} onChange={(e) => setRange(e.target.value)} aria-label="Time range"
          >
            {OBS_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <Link to={deepLink} className="text-xs text-blue-600 hover:underline whitespace-nowrap">
            Open in AI Observability
          </Link>
        </div>
      </div>

      {isError && <p className="text-sm text-ink-muted">Could not load observability.</p>}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        {[
          { label: 'Calls', val: isLoading ? '—' : kpis.calls.toLocaleString() },
          { label: 'Success rate', val: isLoading ? '—' : kpis.successRate },
          { label: 'Avg latency', val: isLoading ? '—' : kpis.avgLatency },
          { label: 'p95 latency', val: isLoading ? '—' : kpis.p95Latency },
          { label: 'Total cost', val: isLoading ? '—' : kpis.totalCost },
        ].map((k) => (
          <div key={k.label} className="rounded bg-gray-50 dark:bg-gray-800 p-2">
            <div className="text-[11px] text-ink-muted">{k.label}</div>
            <div className="text-sm font-semibold text-ink tabular-nums">{k.val}</div>
          </div>
        ))}
      </div>

      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-sm text-ink-muted">No traced queries in this window.</p>
      )}
      {rows.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
          {rows.slice(0, 8).map((r) => {
            const failed = r.status === 'FAILED';
            return (
              <div key={r.observabilityId} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate">{r.question || r.name || 'RAG query'}</div>
                  <div className="text-xs text-ink-muted whitespace-nowrap">{formatDate(r.startedAt)}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 text-xs text-ink-muted">
                  <span className="whitespace-nowrap">{fmtMs(r.latencyMs)}</span>
                  <span className="whitespace-nowrap">{fmtCost(r.cost, r.currency)}</span>
                  <Tag value={failed ? 'failed' : (r.status || 'ok')}
                    severity={failed ? 'danger' : 'success'} className="text-xs px-1.5 py-0.5" />
                  <button type="button" className="text-xs text-blue-600 hover:underline"
                    onClick={() => setOpenTrace(r.observabilityId)}>
                    View trace
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog
        header="Trace detail"
        visible={!!openTrace}
        onHide={() => setOpenTrace(null)}
        style={{ width: '64rem', maxWidth: '95vw' }}
        maximizable
        dismissableMask
      >
        <div className="max-h-[75vh] overflow-auto">
          {openTrace && <ObsTracingDetail observabilityId={openTrace} embedded />}
        </div>
      </Dialog>
    </div>
  );
}
